// =============================================================================
// Origin UI — src/config/authConfig.ts
// MSAL (Microsoft Authentication Library) configuration for Entra ID login.
// These values must match the Entra ID app registration for the UI.
// =============================================================================

import { Configuration, LogLevel } from '@azure/msal-browser';

/**
 * MSAL configuration for the Origin UI app registration.
 * Replace placeholders with actual values from your Entra ID app registration.
 */
export const msalConfig: Configuration = {
  auth: {
    // The Application (client) ID of the UI app registration in Entra ID
    clientId: import.meta.env.VITE_ENTRA_CLIENT_ID || 'YOUR_UI_APP_REGISTRATION_CLIENT_ID',

    // The directory (tenant) ID
    authority: `https://login.microsoftonline.com/${
      import.meta.env.VITE_ENTRA_TENANT_ID || 'YOUR_ENTRA_TENANT_ID'
    }`,

    // Redirect URI after login — must match what's registered in Entra ID
    redirectUri: import.meta.env.VITE_REDIRECT_URI || 'http://localhost:5173',

    // Where to redirect after logout
    postLogoutRedirectUri: import.meta.env.VITE_REDIRECT_URI || 'http://localhost:5173',

    // Required for single-page apps using auth code flow with PKCE
    navigateToLoginRequestUrl: true,
  },
  cache: {
    // sessionStorage clears on tab close (more secure for corporate apps)
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return; // Never log PII
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            break;
          case LogLevel.Warning:
            console.warn(message);
            break;
          case LogLevel.Info:
            // Only log in development
            if (import.meta.env.DEV) console.info(message);
            break;
        }
      },
      logLevel: import.meta.env.DEV ? LogLevel.Info : LogLevel.Warning,
    },
  },
};

/**
 * Scopes to request when acquiring tokens for the Origin API.
 * The scope format is: api://{api-client-id}/{scope-name}
 */
export const apiScopes = {
  // The default scope for the Origin API — grants access to all API endpoints
  // based on the user's roles. Replace with your actual API app registration client ID.
  default: [
    `api://${import.meta.env.VITE_API_CLIENT_ID || 'YOUR_API_APP_REGISTRATION_CLIENT_ID'}/access_as_user`,
  ],
};

/**
 * Login request — what scopes to request during interactive login.
 */
export const loginRequest = {
  scopes: ['openid', 'profile', 'email', ...apiScopes.default],
  prompt: 'select_account' as const,
};
